import React from 'react'
import { Platform } from 'react-native'

const containsNode = (root: unknown, target: EventTarget | null) =>
    !!root && !!target && (root as HTMLElement).contains?.(target as Node)

const movesTrigger = (target: EventTarget | null, trigger: unknown) => {
    if (target === document || target === document.documentElement || target === document.body) {
        return true
    }
    return containsNode(target, trigger as EventTarget | null)
}

/**
 * Dismiss an anchored overlay as soon as the surface holding its trigger scrolls.
 *
 * The anchored position is measured on open and recomputed only on resize, so a
 * scroll that moves the trigger leaves the overlay stranded over unrelated
 * content. Closing is the honest response — the overlay belongs to a trigger the
 * user has just scrolled away from.
 *
 * Only a scroll that actually moves the trigger dismisses: the page itself, or a
 * scroller containing the trigger. The overlay's own content is the user reading
 * it, and an unrelated scroller (an auto-scrolling list in another panel) leaves
 * the trigger where it is — both stay open. The listener is capturing because
 * scroll does not bubble.
 */
export const useDismissOnOutsideScroll = (
    isOpen: boolean,
    triggerRef: React.MutableRefObject<unknown>,
    onClose?: () => void,
) => {
    React.useEffect(() => {
        if (Platform.OS !== 'web' || !isOpen) {
            return undefined
        }
        const onScroll = (event: Event) => {
            if (!movesTrigger(event.target, triggerRef.current)) {
                return
            }
            onClose?.()
        }
        document.addEventListener('scroll', onScroll, true)
        return () => document.removeEventListener('scroll', onScroll, true)
    }, [isOpen, triggerRef, onClose])
}
